import type {
    CrmFieldType,
    ExcludeAccountFields,
    HubspotMappingField,
} from "app/pages/sales-intelligence/common-components/export-table/types";
// fallow-ignore-next-line boundary-violation -- shared sales helper lives in workspace zone; move pending
import { createCancelable } from "app/pages/workspace/sales/helpers";
import type { IFetchService } from "app/services/fetchService";
import { ExportSettingsSourceType } from "../types/shared";
import type { DynamicsPredefinedApiServiceType, DynamicsPreDefinedType } from "./dynamics/predefined";
import { dynamicsPredefinedApiService } from "./dynamics/predefined";
import type { DynamicsStatusServiceType } from "./dynamics/status";
import { dynamicsStatusService } from "./dynamics/status";
import type { DynamicsTogglesServiceType } from "./dynamics/toggles";
import { dynamicsTogglesService } from "./dynamics/toggles";

export type DynamicsServiceType = {
    getDynamicsFields(
        columnName: string,
        type: DynamicsPreDefinedType,
        cacheToken: string,
        sourceType?: ExportSettingsSourceType,
    ): Promise<CrmFieldType[]>;
    getDynamicsColumns(
        type: DynamicsPreDefinedType,
        cacheToken: string,
        cancellation: AbortSignal,
    ): Promise<CrmFieldType[]>;
    getDynamicsExcludeAccountFields(): Promise<ExcludeAccountFields>;
    getDynamicsMappingFields(): Promise<HubspotMappingField[]>;
    putDynamicsStatusFields(externalField: string): Promise<{ value: boolean }>;
    putDynamicsFilterFields(fields: string[]): Promise<{ value: boolean }>;
};

export const dynamicsService = (
    fetchService: IFetchService,
): DynamicsServiceType & DynamicsStatusServiceType & DynamicsTogglesServiceType & DynamicsPredefinedApiServiceType => ({
    getDynamicsFields: createCancelable(
        (
            signal: AbortSignal,
            columnName: string,
            type: DynamicsPreDefinedType,
            cacheToken: string,
            sourceType?: ExportSettingsSourceType,
        ): Promise<CrmFieldType[]> => {
            const params = new URLSearchParams();

            params.set("column", sourceType === ExportSettingsSourceType.Technologies ? "techCategory" : columnName);
            sourceType && params.set("sourceType", sourceType.toString());
            params.set("cacheToken", cacheToken);
            const url = `sales-api/microsoft-dynamics/${type}-applicable-columns?${params.toString()}`;

            return fetchService.get(url, undefined, {
                cancellation: signal,
            });
        },
    ),
    getDynamicsColumns(type: DynamicsPreDefinedType, cacheToken: string, cancellation: AbortSignal) {
        return fetchService.get<CrmFieldType[]>(
            `sales-api/microsoft-dynamics/${type}-columns?cacheToken=${cacheToken}`,
            undefined,
            { cancellation },
        );
    },
    getDynamicsExcludeAccountFields: (): Promise<ExcludeAccountFields> => {
        return fetchService.get("sales-api/microsoft-dynamics/account-text-columns");
    },
    getDynamicsMappingFields: createCancelable((signal: AbortSignal): Promise<HubspotMappingField[]> => {
        return fetchService.get("sales-api/microsoft-dynamics/mapping-field?type=AccountImport", undefined, {
            cancellation: signal,
        });
    }),
    putDynamicsStatusFields: createCancelable(
        (signal: AbortSignal, externalField: string): Promise<{ value: boolean }> => {
            return fetchService.put(
                `sales-api/microsoft-dynamics/mapping-field/import/type?microsoftDynamicsField=${externalField}`,
                undefined,
                { cancellation: signal },
            );
        },
    ),
    putDynamicsFilterFields: createCancelable((signal: AbortSignal, fields: string[]): Promise<{ value: boolean }> => {
        return fetchService.put("sales-api/microsoft-dynamics/mapping-field/import/lists", fields, {
            cancellation: signal,
        });
    }),

    ...dynamicsStatusService(fetchService),
    ...dynamicsTogglesService(fetchService),
    ...dynamicsPredefinedApiService(fetchService),
});
